import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { nomeDoPlano } from "@/lib/planos";

const MAX_ATTEMPTS = 15;
const REDIRECT_DELAY = 4; // segundos

export default function ObrigadoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const { refreshUser } = useAuth();
  const [destino, setDestino] = useState("cadastro"); // cadastro | app | login

  const [status, setStatus] = useState("verifying"); // verifying | success
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  const startedRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      window.location.replace("/#planos");
      return;
    }
    // trava contra multiplas cadeias de polling (StrictMode chama 2x)
    if (startedRef.current) return;
    startedRef.current = true;

    let attempt = 0;
    let cancelled = false;

    let destinoFinal = "cadastro";
    const goToRegister = () => {
      if (cancelled) return;
      if (destinoFinal === "app") navigate("/", { replace: true });
      else if (destinoFinal === "login") navigate("/login", { replace: true });
      else navigate(`/register?session_id=${sessionId}`, { replace: true });
    };

    const fireConversion = (amount, currency, plano) => {
      const key = "fired_" + sessionId;
      if (sessionStorage.getItem(key)) return; // dispara so 1x
      sessionStorage.setItem(key, "1");
      const value = amount != null ? amount : 0;
      const cur = currency || "BRL";
      try {
        if (window.fbq) {
          window.fbq(
            "track",
            "Purchase",
            { value: value, currency: cur, content_name: `Aesthetica Pricing Pro - ${nomeDoPlano(plano) || "Assinatura"}` },
            { eventID: sessionId }
          );
        }
      } catch (e) {}
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "purchase",
          value: value,
          currency: cur,
          transaction_id: sessionId,
        });
      } catch (e) {}
    };

    const startCountdownAndRedirect = () => {
      if (cancelled) return;
      setStatus("success");
      let c = REDIRECT_DELAY;
      setCountdown(c);
      const tick = () => {
        if (cancelled) return;
        c -= 1;
        setCountdown(c);
        if (c <= 0) goToRegister();
        else timeoutRef.current = setTimeout(tick, 1000);
      };
      timeoutRef.current = setTimeout(tick, 1000);
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/stripe/verify-session/${sessionId}`, {
          timeout: 10000,
        });
        if (res.data && res.data.payment_status === "paid") {
          fireConversion(res.data.amount_total, res.data.currency, res.data.plano);
          if (res.data.renovacao) {
            // Renovacao: liga a assinatura nova a conta e volta para o app.
            try {
              await axios.post(`${API}/billing/confirmar`, { session_id: sessionId });
              await refreshUser();
              destinoFinal = "app";
            } catch (e) {
              destinoFinal = "login";
            }
          } else if (res.data.conta_criada) {
            destinoFinal = "login";
          }
          setDestino(destinoFinal);
          startCountdownAndRedirect();
          return;
        }
      } catch (e) {
        // erro/nao confirmado ainda -> tenta de novo COM intervalo
      }
      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) {
        goToRegister(); // desistiu -> vai pro register SEM disparar nada
        return;
      }
      const delay = attempt < 3 ? 2000 : 3000;
      timeoutRef.current = setTimeout(poll, delay);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-teal-700 dark:text-teal-400"
            style={{ fontFamily: "Manrope" }}
          >
            Aesthetica
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pricing Pro</p>
        </div>
        <Card className="border-slate-200 dark:border-slate-800 shadow-lg">
          <CardContent className="pt-8 pb-8 space-y-4">
            {status === "verifying" ? (
              <>
                <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Confirmando seu pagamento...
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  Aguarde, estamos confirmando com o Stripe.
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Obrigado pela sua compra! 🎉
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  {destino === "app"
                    ? `Assinatura renovada! Voltando para o app em ${countdown}s...`
                    : destino === "login"
                    ? `Pagamento confirmado. Abrindo o login em ${countdown}s...`
                    : `Estamos preparando seu acesso. Você será redirecionado em ${countdown}s...`}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
